import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";
import { ogImageMeta } from "@/lib/seo/og-image";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad gör en filmbyrå egentligen?",
    a: "En filmbyrå tar er affärsidé eller marknadsmål och översätter det till film: insights, manus, regi, foto, klipp, grade, ljud och versionering. Fenny gör hela kedjan inhouse — ni jobbar mot en projektledare och ett team istället för att upphandla varje del separat.",
  },
  {
    q: "Vad är skillnaden mellan filmproduktion och videoproduktion?",
    a: "Termerna används ofta synonymt i Sverige. Historiskt syftade filmproduktion på TV/biograf-format och videoproduktion på webbformat — men 2026 producerar samma team för båda. Vi använder filmproduktion när vi pratar om produktioner med strategi och regi, video när det handlar om snabba content-uppdrag.",
  },
  {
    q: "Hur väljer vi rätt filmbyrå?",
    a: "Tre filter: (1) titta på case i er bransch, inte bara på showreelen, (2) be om referenser från kunder som jobbat med byrån i 12+ månader — det säger mer än enskilda produktioner, (3) testa kemin på första mötet. En filmbyrå är en partner, inte en leverantör.",
  },
  {
    q: "Var i Sverige jobbar Fenny?",
    a: "Vi producerar i hela Sverige, med tyngdpunkt i Stockholm, Göteborg och Malmö. Resa och logistik är inkluderat i Pulse-abonnemang. För Prime-produktioner specificeras detta i offerten.",
  },
  {
    q: "Hur snabbt kan ni vara igång?",
    a: "Första mötet inom en vecka. Pulse-kunder har första klippet ute inom 10–14 dagar. Prime-produktioner planeras från brief till leverans på 3–6 veckor beroende på komplexitet.",
  },
  {
    q: "Vad kostar filmproduktion?",
    a: "Spannet är brett — från 25 kkr för en enklare social-first-film till 900 kkr för en TV-kampanj med skådespelare. Fenny Pulse börjar från fast månadsbudget för löpande output. Vi prissätter på effekt och volym, inte på timmar.",
  },
];

export const Route = createFileRoute("/filmproduktion")({
  head: () => ({
    meta: [
      { title: "Filmproduktion — premium filmbyrå i Sverige | Fenny" },
      {
        name: "description",
        content:
          "Fenny är en svensk filmbyrå för premium filmproduktion — reklam, brand, employer branding och löpande content. Strategi, regi, foto och klipp i en process.",
      },
      { property: "og:title", content: "Filmproduktion — Fenny" },
      { property: "og:description", content: "Premium filmproduktion för reklam, brand och employer branding. Hela kedjan i en process." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/filmproduktion" },
      ...ogImageMeta({ hub: "filmproduktion", alt: "Fenny — filmproduktion" }),
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/filmproduktion" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/filmproduktion", name: "Filmproduktion — premium filmbyrå i Sverige | Fenny", description: "Fenny är en svensk filmbyrå för premium filmproduktion — reklam, brand, employer branding och löpande content. Strategi, regi, foto och klipp i en process."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Filmproduktion",
            path: "/filmproduktion",
            serviceType: "Filmproduktion",
            description:
              "Premium filmproduktion i Sverige — reklamfilm, företagsfilm, brand storytelling och employer branding. Strategi, regi, foto, klipp, grade och versionering i en process.",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Filmproduktion", path: "/filmproduktion" },
          ]),
        ),
      },
    ],
  }),
  component: FilmproduktionPage,
});

function FilmproduktionPage() {
  return (
    <LandingPage
      quickAnswerId="sv_vad_kostar_filmproduktion"
      eyebrow="Filmproduktion"
      title={<>Filmproduktion utan byråspråk och utan månader av väntan.</>}
      lede="Fenny är en svensk filmbyrå med fullt produktionsteam — regi, foto, ljud, klipp, grade och versionering. Vi producerar reklamfilm, brand storytelling, företagsfilm och employer branding. Med Pulse får ni löpande output, med Prime gör vi kampanjer."
      tldr={<p>Filmproduktion innebär hela kedjan från strategi och manus till inspelning, klipp, grade, ljud och versionering. Fenny levererar som <strong>filmabonnemang (Pulse, från 14 995 kr/mån)</strong> för löpande flöde eller <strong>fastpris-projekt (Prime)</strong> för kampanjer. Bas i Stockholm, jobbar i hela Sverige och Norden.</p>}
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Se case", to: "/case" }}
      trackingId="filmproduktion"
      relatedLinksFor="/filmproduktion"
      trust={[
        { value: "25–900 kkr", label: "spann per produktion" },
        { value: "10–14 dagar", label: "till första klipp i Pulse" },
        { value: "Hela Sverige", label: "resa ingår i Pulse" },
      ]}
      pricing={{
        heading: "Vad filmproduktion kostar 2026",
        intro: "Riktpriser för de vanligaste uppdragen. Prime är fast projektpris, Pulse är fast månadsbudget för löpande output.",
        rows: [
          { format: "Social-content", scope: "1 dag, 6–10 klipp i alla format med textning", price: "45 000–115 000 kr", lead: "2–3 veckor" },
          { format: "Företags- och kundcasefilm", scope: "1–2 dagar, intervju, b-roll, master + cutdowns", price: "60 000–250 000 kr", lead: "3–5 veckor" },
          { format: "Kampanj- och brandfilm", scope: "2–4 dagar, regi, casting, originalmusik", price: "250 000–900 000 kr", lead: "6–12 veckor" },
          { format: "Pulse-abonnemang", scope: "Löpande produktion, strategi och versionering inom fast budget", price: "från 14 995 kr/mån", lead: "första klipp 10–14 dagar" },
        ],
        footnote: "Exkl. moms. Filmtillstånd, gager och musiklicenser specificeras separat i offerten.",
      }}
      objections={{
        heading: "Innan ni väljer produktionsbolag",
        items: [
          { objection: "Hur vet vi att fast pris håller?", answer: <>Offerten specificerar antal inspelningsdagar, versioner och revisionsrundor. Ändrar ni omfånget får ni ett skriftligt tillägg innan vi fortsätter — inga överraskningar på slutfakturan.</> },
          { objection: "Vem äger materialet?", answer: <>Ni äger de mastrade filerna med full nyttjanderätt. Råmaterial lagras tolv månader och kan licensieras separat.</> },
          { objection: "Ska vi välja projekt eller abonnemang?", answer: <>Behöver ni en enskild kampanjfilm: Prime. Behöver ni innehåll varje månad: Pulse — styckpriset blir märkbart lägre och ni slipper offertrundor.</> },
        ],
      }}
      bullets={[
        { title: "Hela kedjan", body: "Strategi, manus, regi, foto, ljud, klipp, grade, ljudmix, versionering — utan att upphandla varje del separat." },
        { title: "Sverige-täckande", body: "Vi producerar i hela landet med tyngdpunkt Stockholm, Göteborg och Malmö. Resa ingår i Pulse-abonnemang." },
        { title: "Två modeller", body: "Pulse för löpande månads-output, Prime för kampanjer och större produktioner. Många kunder kombinerar." },
      ]}
      sections={[
        {
          id: "vad-vi-producerar",
          heading: "Vilka typer av filmproduktion gör vi?",
          body: (
            <>
              <p>
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">Reklamfilm</a> för paid social, YouTube och TV. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foretagsfilm">Företagsfilm</a> som faktiskt blir sedd. Brand storytelling och dokumentär. Employer branding och <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/case">rekryteringsfilm</a>. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/produktfilm">Produktfilm</a> för e-handel och B2B. Kundcase, eventfilm och utbildningsfilm.
              </p>
              <p>
                Den röda tråden är att vi alltid börjar med vad ni vill att en mottagare ska göra, känna eller komma ihåg — och bygger filmen mot det. Inte tvärtom.
              </p>
            </>
          ),
        },
        {
          id: "pulse-vs-prime",
          heading: "Pulse eller Prime — vilken modell passar?",
          body: (
            <>
              <p>
                <strong>Pulse</strong> är filmproduktion som abonnemang: fast månadsbudget, 4–20 färdiga filmer per månad, första klippet ute inom 10–14 dagar. Bäst för marknadsteam som behöver kontinuerligt content.
              </p>
              <p>
                <strong>Prime</strong> är skräddarsydda projekt: kampanjfilm, brand-film, jubileum, event. Fast projektpris från brief till leverans. Bäst när insatsen är stor och tydligt avgränsad.
              </p>
              <p>
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/jamfor/pulse-vs-prime">Jämför Pulse vs Prime →</a>
              </p>
            </>
          ),
        },
        {
          id: "geografi",
          heading: "Var i Sverige jobbar vi?",
          body: (
            <p>
              Fenny är Sverige-täckande. Vi har lokala team i de tre storstäderna: filmproduktion i <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/stockholm">Stockholm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/goteborg">Göteborg</a> och <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/malmo">Malmö</a>. För Pulse-abonnemang är resa och logistik inom Sverige inkluderat.
            </p>
          ),
        },
        {
          id: "pris",
          heading: "Vad kostar en filmproduktion?",
          body: (
            <p>
              Vi prissätter på effekt och leveransvolym. Pulse börjar från fast månadsbudget, Prime på fast projektpris. <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/vad-kostar-en-foretagsfilm">Läs mer om vad filmproduktion kostar</a> eller <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/priser">räkna fram er nivå direkt</a>.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={6}
      riskReducers
      leadMagnet={{
        slug: "brief-mall",
        title: "Ladda ner: brief-mallen som inte slösar tid",
        description: "1 sida, 7 fält som styr utfallet, plus ett ifyllt exempel. Använd den innan ni tar in offerter.",
      }}
      closing={{
        title: "Behöver ni en filmbyrå som faktiskt levererar?",
        body: "30 minuter där vi går igenom era mål och pekar ut vad vi skulle göra först.",
        primary: { label: "Boka strategimöte", to: "/kontakt" },
        secondary: { label: "Räkna pris", to: "/priser" },
      }}
    />
  );
}
